import clsx from 'clsx'
import { useEffect, useState } from 'react'
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form'
import { useQueryClient } from 'react-query'

import { Button } from '@wartek-id/button-v1'
import { Checkbox } from '@wartek-id/checkbox-v1'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Input } from '@wartek-id/input'
import { Select } from '@wartek-id/select'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'
import { useToast } from '@wartek-id/toast-v2'

import {
  API_PATH_PREDIKAT_KINERJA_ORGANISASI,
  useAPIUpdatePredikatKinerjaOrganisasi,
} from 'api/kinerja/useAPIKinerja'
import { ORGANIZATION_PREDICATE_RECOMMENDATION_RESULT_TEXT } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/predicateRecommendation'

import type { TPredicateRecommendationResult } from 'types/kinerja-pelaksanaan'

export type DialogUbahPredikatOrganisasiProps = {
  id: string
  initialValue: TPredicateRecommendationResult
  isOpen: boolean
  isSent: boolean
  npsn: string
  schoolName: string
  onClose?: () => void
}

type FormValues = {
  newPredikatKinerja: TPredicateRecommendationResult
  reason?: string
  confirmation?: string
}

export type DialogStep = 'INITIAL_CONFIRMATION' | 'FORM' | 'FINAL_CONFIRMATION'

export function DialogUbahPredikatOrganisasi({
  id,
  initialValue,
  isOpen,
  isSent,
  npsn,
  schoolName,
  onClose,
}: DialogUbahPredikatOrganisasiProps) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const [step, setStep] = useState<DialogStep>('INITIAL_CONFIRMATION')
  const formMethods = useForm<FormValues>({ mode: 'onChange' })

  const upsertMutation = useAPIUpdatePredikatKinerjaOrganisasi()

  const handleSubmit = (formValues: FormValues) => {
    upsertMutation.mutate(
      {
        tahapPredikatId: id,
        predicate: formValues.newPredikatKinerja,
        reason: formValues.reason ?? '',
        npsn,
      },
      {
        onError: () => {
          onClose?.()
          toast({
            message: 'Gagal ubah Predikat Kinerja Organisasi. Coba lagi.',
          })
        },
        onSuccess: () => {
          queryClient.invalidateQueries(API_PATH_PREDIKAT_KINERJA_ORGANISASI)

          onClose?.()
          toast({
            message: 'Berhasil ubah Predikat Kinerja Organisasi!',
            type: 'success',
          })
        },
      }
    )
  }

  // Reset step upon close
  useEffect(() => {
    if (!isOpen) {
      setStep('INITIAL_CONFIRMATION')
      formMethods.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  return (
    <Dialog
      isOpen={isOpen}
      closeOnOverlayClick={false}
      size="sm"
      onClose={onClose}
    >
      <DialogOverlay />
      <DialogContent>
        <FormProvider {...formMethods}>
          {step === 'INITIAL_CONFIRMATION' && (
            <StepInitialConfirmation
              isSent={isSent}
              schoolName={schoolName}
              onNext={() => setStep('FORM')}
              onBack={onClose}
            />
          )}

          {step === 'FORM' && (
            <StepForm
              schoolName={schoolName}
              initialValue={initialValue}
              onNext={() => setStep('FINAL_CONFIRMATION')}
              onBack={onClose}
            />
          )}

          {step === 'FINAL_CONFIRMATION' && (
            <StepFinalConfirmation
              schoolName={schoolName}
              initialValue={initialValue}
              isLoading={upsertMutation.isLoading}
              onSubmit={handleSubmit}
              onBack={() => setStep('FORM')}
            />
          )}
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}

export const INITIAL_CONFIRMATION_CHECKBOX =
  'Saya mengerti dampak perubahan dan ingin lanjut mengubah Predikat Kinerja Organisasi.'

type StepInitialConfirmationProps = {
  isSent: boolean
  schoolName: string
  onBack: () => void
  onNext: () => void
}
function StepInitialConfirmation({
  isSent,
  schoolName,
  onBack,
  onNext,
}: StepInitialConfirmationProps) {
  const [isConfirmed, setConfirmed] = useState(false)

  return (
    <div className="p-6 space-y-9">
      <div className="flex flex-col space-y-4">
        {isSent && (
          <Icon fontSize="lg" color="critical">
            warning
          </Icon>
        )}
        <Text variant="display-sm" as="h2">
          {isSent
            ? `Predikat Kinerja Organisasi sudah dikirim ke ${schoolName}`
            : 'Perubahan akan berdampak ke Predikat Kinerja Pegawai di satuan pendidikan ini'}
        </Text>

        <Text variant="body-lg" color="subdued">
          Pastikan Anda sudah memahami kondisi di satuan pendidikan dan
          berdiskusi dengan Kepala Sekolah.
        </Text>

        <Checkbox
          checked={isConfirmed}
          className="mb-4"
          labelPosition="start"
          onChange={() => setConfirmed(!isConfirmed)}
        >
          {INITIAL_CONFIRMATION_CHECKBOX}
        </Checkbox>
      </div>
      <div className="flex space-x-3 !justify-start">
        <Button color="white" size="lg" onClick={onBack}>
          Kembali
        </Button>
        <Button
          color="blue"
          disabled={!isConfirmed}
          size="lg"
          type="button"
          onClick={() => onNext?.()}
        >
          Lanjut ubah
        </Button>
      </div>
    </div>
  )
}

type StepFormProps = {
  schoolName: string
  initialValue: TPredicateRecommendationResult
  onBack: () => void
  onNext: () => void
}
function StepForm({ initialValue, schoolName, onBack, onNext }: StepFormProps) {
  const { control, register, formState, handleSubmit } = useFormContext()

  return (
    <>
      <DialogHeader title="Ubah Predikat Kinerja Organisasi" />
      <form className="p-6 space-y-4" onSubmit={handleSubmit(onNext)}>
        <PanelPredikatKinerjaOrganisasi
          state="INITIAL"
          value={initialValue}
          schoolName={schoolName}
        />
        <fieldset className="space-y-1 flex flex-col">
          <Text as="label" htmlFor="updated">
            Anda ingin ubah menjadi:
          </Text>
          <Controller
            control={control}
            rules={{ required: true }}
            name="newPredikatKinerja"
            render={({ field: { onChange, value } }) => (
              <Select
                className="whitespace-nowrap"
                id="updated"
                value={value}
                onChange={(e) => onChange(e.target.value)}
              >
                <option disabled className="text-disabled" selected value="">
                  Pilih di sini
                </option>
                {Object.entries(
                  ORGANIZATION_PREDICATE_RECOMMENDATION_RESULT_TEXT
                ).map(([value, label]) => (
                  <option value={value} key={value}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          />
        </fieldset>
        <fieldset className="space-y-1">
          <Text as="label" htmlFor="reason">
            Apa alasan Anda mengubah? (Opsional)
          </Text>
          <Input
            id="reason"
            placeholder="Ketik di sini"
            {...register('reason')}
          />
        </fieldset>

        <div className="flex space-x-3 justify-end">
          <Button color="white" size="lg" onClick={onBack}>
            Batal
          </Button>
          <Button
            color="blue"
            disabled={!formState.isValid}
            size="lg"
            type="submit"
          >
            Lanjut
          </Button>
        </div>
      </form>
    </>
  )
}

type StepFinalConfirmationProps = {
  schoolName: string
  initialValue: TPredicateRecommendationResult
  isLoading: boolean
  onBack: () => void
  onSubmit: (params: FormValues) => void
}
function StepFinalConfirmation({
  initialValue,
  schoolName,
  isLoading,
  onBack,
  onSubmit,
}: StepFinalConfirmationProps) {
  const { getValues, register, formState, handleSubmit, resetField } =
    useFormContext()

  useEffect(() => {
    return () => resetField('confirmation')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const confirmationText = `Saya setuju untuk mengubah Predikat Kinerja Organisasi ${schoolName}.`

  return (
    <form className="p-6" onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-1 mb-5">
        <Text variant="heading-md" as="h2">
          Berikut perubahan yang Anda lakukan:
        </Text>
        <Text color="subdued">
          Anda dapat ubah lagi jika diperlukan, selama belum dikirim ke Kepala
          Sekolah.
        </Text>
      </div>

      <div className="space-y-4 mb-8">
        <PanelPredikatKinerjaOrganisasi
          schoolName={schoolName}
          state="INITIAL"
          value={initialValue}
        />
        <PanelPredikatKinerjaOrganisasi
          schoolName={schoolName}
          state="UPDATED"
          value={getValues('newPredikatKinerja')}
        />
        <div className="p-4 rounded bg-gray-10 space-y-3">
          <Text variant="body-bold">Apa alasan Anda mengubah? (Opsional)</Text>
          <Text>{getValues('reason') || '-'}</Text>
        </div>
      </div>

      <div className="mb-4">
        <Text variant="body-lg" color="subdued" className="mb-2">
          Mohon ketik kalimat ini pada kolom kalimat persetujuan:
        </Text>
        <div className="bg-blue-5 text-blue-60 p-1 rounded select-none mb-4">
          <Text variant="heading-sm" color="inherit">
            {confirmationText}
          </Text>
        </div>
        <fieldset>
          <div className="space-y-1">
            <Text as="label" htmlFor="uncompleted-confirmation-text">
              Kalimat persetujuan
            </Text>
            <Textarea
              id="uncompleted-confirmation-text"
              placeholder="Ketik di sini."
              autoComplete="false"
              rows={3}
              {...register('confirmation', {
                required: true,
                validate: (value) => value === confirmationText,
              })}
            />
          </div>
        </fieldset>
      </div>

      <div className="flex space-x-3 !justify-start">
        <Button color="white" size="lg" disabled={isLoading} onClick={onBack}>
          Kembali
        </Button>
        <Button
          color="blue"
          disabled={!formState.isValid || isLoading}
          size="lg"
          type="submit"
        >
          Simpan
        </Button>
      </div>
    </form>
  )
}

type PanelPredikatKinerjaOrganisasi = {
  schoolName: string
  state: 'INITIAL' | 'UPDATED'
  value: TPredicateRecommendationResult
}
function PanelPredikatKinerjaOrganisasi({
  schoolName,
  state,
  value,
}: PanelPredikatKinerjaOrganisasi) {
  const labelId =
    state === 'UPDATED' ? 'updated-value-label' : 'initial-value-label'

  return (
    <div className="space-y-1">
      <Text as="label" id={labelId}>
        {state === 'UPDATED'
          ? 'Anda ingin ubah menjadi:'
          : 'Predikat Kinerja Organisasi yang direkomendasikan:'}
      </Text>
      <div className="flex">
        <div className="w-full h-12 px-4 py-1 rounded-l border border-gray-70 flex items-center">
          <Text
            variant="heading-sm"
            aria-label={schoolName}
            className="overflow-wrap-anywhere line-clamp-2"
          >
            {schoolName}
          </Text>
        </div>
        <div
          className={clsx(
            'flex-shrink-0 h-12 w-44 px-6 py-1 flex items-center rounded-r min-w-0',
            state === 'INITIAL' ? 'bg-gray-90' : 'bg-blue-70'
          )}
        >
          <Text
            variant="heading-sm"
            color="inverse"
            className="overflow-wrap-anywhere line-clamp-2"
            aria-labelledby={labelId}
          >
            {ORGANIZATION_PREDICATE_RECOMMENDATION_RESULT_TEXT[value] ?? '-'}
          </Text>
        </div>
      </div>
    </div>
  )
}
